import { Inject, Injectable } from '@angular/core';
import {
    getFirestore,
    Firestore,
    serverTimestamp,
    Timestamp,
    Query,
    collection,
    Transaction,
    doc,
    getDoc,
    setDoc,
    deleteDoc,
    DocumentReference,
    addDoc,
    getDocs,
} from 'firebase/firestore';
import { FSSnap } from '../utils/firestore/fs-snap';
import { FireService } from './fire.service';

/**
 * Service for interacting with Firestore.
 *
 * @remarks
 * This service provides methods for getting, setting, and deleting documents in Firestore.
 */
@Injectable({
    providedIn: 'root',
})
export class FirestoreService {
    /**
     * Default BiViz Firestore database.
     */
    defaultDb: Firestore;
    /**
     * BIJobs Firestore database.
     */
    jobsDb: Firestore;

    /**
     * Creates an instance of FirestoreService.
     *
     * @param fire - An instance of FireService.
     * @param databaseId - An optional string representing the database name to use.
     */
    constructor(fire: FireService) {
        this.defaultDb = getFirestore(fire.app);
        this.jobsDb = getFirestore(fire.app, 'jobs');
    }
    // constructor(
    //     fire: FireService,
    //     @Inject(FirestoreService) databaseId: string
    // ) {
    //     this.firestore =
    //         databaseId == '(default)'
    //             ? getFirestore(fire.app)
    //             : getFirestore(fire.app, databaseId);
    // }

    /**
     * Returns the current server timestamp.
     *
     * @returns A Timestamp representing the current server timestamp.
     */
    static getServerTimestamp(): Timestamp {
        return serverTimestamp() as Timestamp;
    }

    /**
     * Gets a single document from Firestore.
     * @param path - The path of the document.
     * @param tran - An optional Transaction to run this operation inside.
     * @returns A Promise that resolves to an FSSnap containing the document data (typed as T) and snapshot.
     */

    static async getSnap<T extends { [x: string]: any }>(
        firestore: Firestore,
        path: string,
        tran?: Transaction
    ): Promise<FSSnap<T>> {
        const ref = doc(firestore, path);
        const snap = await (tran ? tran.get(ref) : getDoc(ref));
        return {
            data: snap.data() as T,
            snap: snap,
        };
    }

    static async getSnaps<T extends { [x: string]: any }>(
        query: Query
    ): Promise<FSSnap<T>[]> {
        const snap = await getDocs(query);
        snap;
        return snap.docs.map((doc) => {
            return {
                data: doc.data() as T,
                snap: doc,
            };
        });
    }

    /**
     * Sets a document in Firestore.
     *
     * @param snap - An FSSnap containing the document data and snapshot.
     * @returns A Promise that resolves when the operation is complete.
     */
    static setSnap<T extends { [x: string]: any }>(
        snap: FSSnap<T>
    ): Promise<void> {
        return setDoc(snap.snap.ref, snap.data);
    }

    /**
     * Deletes a document in Firestore.
     *
     * @param snap - An FSSnap containing the document data and snapshot.
     * @returns A Promise that resolves when the operation is complete.
     */
    static deleteSnap<T extends { [x: string]: any }>(
        snap: FSSnap<T>
    ): Promise<void> {
        return deleteDoc(snap.snap.ref);
    }

    /**
     * Sets a document in Firestore.
     *
     * @param path - The path of the document.
     * @param t - The data to set.
     * @param tran - An optional Transaction to run this operation inside.
     * @returns A Promise that resolves when the operation is complete.
     */
    static async setDoc<T extends { [x: string]: any }>(
        firestore: Firestore,
        path: string,
        t: T,
        tran?: Transaction
    ): Promise<void> {
        const ref = doc(firestore, path);
        if (tran) {
            tran.set(ref, t);
            return;
        }
        await setDoc(ref, t);
    }

    /**
     * Adds a document to a collection in Firestore.
     *
     * @param collectionPath - The path of the collection.
     * @param t - The data to add.
     * @returns A Promise that resolves to a DocumentReference pointing to the newly added document.
     */
    static addDoc<T extends { [x: string]: any }>(
        firestore: Firestore,
        collectionPath: string,
        t: T
    ): Promise<DocumentReference> {
        return addDoc(collection(firestore, collectionPath), t);
    }

    /**
     * Deletes a document in Firestore.
     *
     * @param path - The path of the document.
     * @returns A Promise that resolves when the operation is complete.
     */
    static deleteDoc(firestore: Firestore, path: string): Promise<void> {
        return deleteDoc(doc(firestore, path));
    }
}

// export interface GetOptions {
//     limit?: number;
//     startAfter?: firebase.firestore.DocumentSnapshot;
//     endBefore?: firebase.firestore.DocumentSnapshot;
//     orderBy?: string | firebase.firestore.FieldPath;
//     orderByDirection?: 'asc' | 'desc';
//     where?: GetOptionsWhere[];
// }

// export interface GetOptionsWhere {
//     fieldPath: string | firebase.firestore.FieldPath;
//     opStr: firebase.firestore.WhereFilterOp;
//     value: any;
// }
